/**
 * The learner's place in a book, as the player holds it.
 *
 * The rules live in `#/lib/flashbookProgress` and are pure; this is the part that
 * has to know about *time* — when to write, and what to do with a write that is
 * still in the air when the player goes away.
 *
 * Three decisions shape it:
 *
 *  - **Writes are coalesced, not skipped.** A hundred-page book is answered a
 *    hundred times, and the position changes on every page turn; writing the whole
 *    read to storage on each one would be a device write per tap for no gain, so
 *    changes are gathered and written a moment later. Nothing is *dropped* by
 *    doing this: every change is folded into the same stored value, so the last
 *    write already contains all of them.
 *  - **The newest value is the one in hand.** React state is a render's snapshot,
 *    and a save that arrives between two taps would otherwise chain off a stale
 *    read and lose the answer that just landed. The pending value is kept in a ref
 *    as well, and every step folds into that.
 *  - **Leaving flushes.** A learner who closes the book — or the app — must find
 *    their place next time, so unmounting writes immediately rather than waiting
 *    out a timer that will never fire.
 *
 * A player that is *previewing* a book never opens a read at all: preview is the
 * studio, and a creator sampling their own book is not a learner reading it. That
 * is what `enabled` is for, and the pure layer's no-op-on-no-open-read is what
 * makes it safe for the player to keep calling these regardless.
 *
 * ## And the copy that follows the learner
 *
 * The place is also carried to the learner's own repo, so the book can be picked
 * up on another device (see `#/lib/flashbookProgressSync`). That half is written on
 * a *slower* clock than the device half, and deliberately: storage is on the
 * machine and costs nothing, while a repo write is a request, and a page turn is
 * not worth one. So the same coalescing happens twice — 400ms to the disk, three
 * seconds to the network — and both are flushed on the way out.
 *
 * The remote is consulted **once**, on mount, and what it is allowed to do is
 * narrow: it can fill in a device that has no place of its own, and it can never
 * take one away. That is the "device copy wins" rule, and it lives in the pure
 * layer where it can be tested case by case. What is here is the plumbing around
 * it — fetching it before the learner has had time to act on a cover that says
 * "start reading", writing the winner back so the *next* device hears it, and
 * saying nothing at all when there is no session to say it with.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {type AtpAgent} from '@atproto/api'

import {
  beginRead,
  emptyProgress,
  finishRead,
  type FlashBookOpenRead,
  type FlashBookReadProgress,
  type FlashBookReadSummary,
  type FlashBookResumeTarget,
  openReadPct,
  recordPractice,
  resumeRead,
  resumeTarget,
  saveReadPosition,
  summariseProgress,
} from '#/lib/flashbookProgress'
import {
  getFlashBookProgress,
  putFlashBookProgress,
} from '#/lib/flashbookProgressStore'
import {mergeRemoteRead, sameOpenRead} from '#/lib/flashbookProgressSync'
import {
  fetchFlashBookProgress,
  pushFlashBookProgress,
} from '#/state/queries/flashbook-progress'
import {type FlashBookPage, type FlashBookPageResult} from '#/types/flashbook'

/**
 * How long a change waits to be written.
 *
 * Short enough that a reader who closes the app a second after answering finds
 * their answer, long enough that a page of questions is one write instead of
 * one per question.
 */
const WRITE_DELAY_MS = 400

/**
 * How long a change waits before it is offered to the learner's repo.
 *
 * Seven times the device delay, and the difference is the point: a page turn is
 * worth writing to the machine under the learner's hands, and is not worth a
 * request. Long enough that a page of questions is one write, short enough that a
 * learner who closes the book and picks up their phone finds the page they were
 * on rather than the one before it.
 */
const SYNC_DELAY_MS = 3000

export function useFlashBookProgress({
  bookUri,
  bookTitle,
  pages,
  enabled,
  agent,
}: {
  bookUri: string
  bookTitle: string
  pages: readonly FlashBookPage[]
  /** False for a preview, or for a fixture reading a book on somebody's behalf. */
  enabled: boolean
  /**
   * The learner's agent, for carrying the place between devices.
   *
   * Taken as a prop rather than read from the session context, because the reader
   * itself is reachable signed out — and a hook that reached for a context would
   * have to be wrapped by every surface that renders a book. `null` and a session-
   * less agent both mean the same thing here: this device keeps its own copy.
   */
  agent: AtpAgent | null
}) {
  /** Read once, on mount: storage is synchronous, so the first frame can know. */
  const [progress, setProgress] = useState<FlashBookReadProgress | null>(() =>
    enabled ? getFlashBookProgress(bookUri) : null,
  )
  const latest = useRef<FlashBookReadProgress | null>(progress)
  /** The read this player is *in*, which is what a finish is tied back to. */
  const readId = useRef<string | null>(progress?.open?.id ?? null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unwritten = useRef(false)
  const mounted = useRef(true)
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * The read the learner's repo was last told about.
   *
   * A ref rather than a boolean, so "has anything changed" can be answered with the
   * same comparison the merge uses — and so a push that *failed* leaves the
   * remembered value alone, which is what makes the next write retry it without a
   * retry loop of its own.
   */
  const pushedOpen = useRef<FlashBookOpenRead | null>(null)

  /*
   * The two things a stable callback needs, in refs.
   *
   * The push below has to be the *same function* for the life of the player: it is
   * called from the unmount cleanup, and a callback whose identity moved with the
   * session would make that effect run its cleanup — and push — every time the
   * agent changed. Assigned in an effect rather than during render, which is what
   * React asks for and what keeps the value current without a second render.
   */
  const agentRef = useRef<AtpAgent | null>(agent)
  const enabledRef = useRef(enabled)
  useEffect(() => {
    agentRef.current = agent
    enabledRef.current = enabled
  }, [agent, enabled])

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const value = latest.current
    if (!value || !unwritten.current) return
    unwritten.current = false
    putFlashBookProgress(value)
  }, [])

  /**
   * Offer this device's place to the learner's repo — if it has changed, and if
   * there is a repo to offer it to.
   *
   * The read that was *last* accepted is remembered, so this is safe to call as
   * often as the caller likes: an unchanged place is not rewritten, and a write
   * that failed is retried at the next flush because nothing was remembered.
   *
   * `force` is for the one caller that cannot afford to be talked out of it —
   * ending a read (see `finish`). The guard above answers "is there anything new to
   * say?", and what a finish has to say is that the place is *gone*; but "we never
   * wrote a place" and "the repo holds nothing" look identical to it, because the
   * remembered value only knows what was written. So a read that ended after a
   * write that never landed — a lost connection, a PDS that refused — would leave
   * its stale record in the repo, and the learner's next device would offer to
   * continue a read that this one had already finished.
   */
  const sync = useCallback((force = false) => {
    if (syncTimer.current) {
      clearTimeout(syncTimer.current)
      syncTimer.current = null
    }
    const value = latest.current
    const session = agentRef.current
    if (!enabledRef.current || !session?.session || !value) return
    const open = value.open
    if (
      !force &&
      ((open && pushedOpen.current && sameOpenRead(open, pushedOpen.current)) ||
        (!open && !pushedOpen.current))
    ) {
      return
    }
    void pushFlashBookProgress(session, value).then(landed => {
      if (landed) pushedOpen.current = open ?? null
    })
  }, [])

  /*
   * The remote's one chance: read on mount, before the learner has had time to act
   * on a cover that says "start reading". Deliberately not a dependency of anything
   * that runs again — a book is opened once, and a re-read of the record mid-book
   * could only fight with the copy in hand.
   */
  useEffect(() => {
    if (!enabled || !agent?.session) return
    let cancelled = false
    void fetchFlashBookProgress(agent, bookUri).then(remote => {
      if (cancelled) return
      const decision = mergeRemoteRead({
        local: latest.current,
        remote,
        bookUri,
        bookTitle,
      })
      if (decision.adopted && decision.progress) {
        latest.current = decision.progress
        pushedOpen.current = decision.progress.open
        putFlashBookProgress(decision.progress)
        if (mounted.current) setProgress(decision.progress)
        return
      }
      // The device's copy won — and if the record disagreed, the winner is what the
      // learner's *next* device should hear.
      if (decision.push) sync()
    })
    return () => {
      cancelled = true
    }
  }, [agent, bookTitle, bookUri, enabled, sync])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      // Unmounting is the one moment a reader is guaranteed to be leaving, so it
      // writes rather than trusting a timer that is about to be discarded — both
      // copies of the place, for the same reason.
      flush()
      sync()
    }
  }, [flush, sync])

  const apply = useCallback(
    (next: FlashBookReadProgress) => {
      latest.current = next
      unwritten.current = true
      if (mounted.current) setProgress(next)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(flush, WRITE_DELAY_MS)
      if (syncTimer.current) clearTimeout(syncTimer.current)
      syncTimer.current = setTimeout(sync, SYNC_DELAY_MS)
    },
    [flush, sync],
  )

  /**
   * Start a read from the beginning.
   *
   * Registers the read it sets aside (see `beginRead`), so a learner who chooses
   * to start over is recorded as having done so rather than having never been
   * there.
   */
  const begin = useCallback(() => {
    if (!enabled) return null
    const base = latest.current ?? emptyProgress(bookUri, bookTitle)
    const next = beginRead(base, {pages})
    readId.current = next.open?.id ?? null
    apply(next)
    return readId.current
  }, [apply, bookTitle, bookUri, enabled, pages])

  /**
   * Continue a read, against the book as it is now.
   *
   * Adopts the open read's identity, so answers given before the break and after
   * it belong to the same read — which is the whole point of continuing rather
   * than starting again.
   */
  const adopt = useCallback(
    (target: FlashBookResumeTarget) => {
      if (!enabled) return null
      const base = latest.current
      if (!base) return null
      readId.current = target.sessionId
      apply(resumeRead(base, {pages}))
      return target.sessionId
    },
    [apply, enabled, pages],
  )

  /** Save where the reader is, and what they have answered so far. */
  const note = useCallback(
    ({
      pageIndex,
      pageResults,
      timeMs,
    }: {
      pageIndex: number
      pageResults: FlashBookPageResult[]
      timeMs: number
    }) => {
      const id = readId.current
      const base = latest.current
      if (!enabled || !id || !base) return
      apply(
        saveReadPosition(base, {
          sessionId: id,
          pageIndex,
          pageResults,
          timeMs,
          bookTitle,
        }),
      )
    },
    [apply, bookTitle, enabled],
  )

  /**
   * End the read at the report.
   *
   * Keyed by the read's id, so finishing twice — which happens when a learner
   * goes back into the book from a question the report named — corrects the same
   * read instead of registering a second one. The id is deliberately *kept*
   * afterwards, because that second finish is still this read.
   *
   * Returns the read's id — or `null` when nothing was open — because the ending
   * is what a practice is *about*: the questions a report names belong to one read,
   * and registering the practice against it is what keeps the two comparable.
   */
  const finish = useCallback(
    ({
      pageResults,
      timeMs,
      pageReached,
      completed = true,
    }: {
      pageResults: FlashBookPageResult[]
      timeMs: number
      pageReached?: number
      completed?: boolean
    }): string | null => {
      const id = readId.current
      const base = latest.current
      if (!enabled || !id || !base) return null
      apply(
        finishRead(base, {
          sessionId: id,
          pages,
          pageResults,
          timeMs,
          pageReached,
          completed,
          bookTitle,
        }),
      )
      // The register is the point of the feature, so it does not wait on a timer —
      // and neither does the *clearing* of the place, which is what stops another
      // device offering to continue a read that ended here. Forced, because the
      // clear is the claim: accepting "nothing new to say" here is exactly how a
      // stale record survives a finish.
      flush()
      sync(true)
      return id
    },
    [apply, bookTitle, enabled, flush, pages, sync],
  )

  /**
   * Register the questions a report named, sat once more.
   *
   * Deliberately not a read: it opens nothing and finishes nothing, and the read
   * it took its questions from is already in the register. The write is immediate
   * for the same reason a finish is — the practice *is* the thing being recorded,
   * and waiting out a timer to say so would lose it to a tab closed on the last
   * question.
   */
  const practice = useCallback(
    ({
      startedAt,
      pages,
      results,
      ofSessionId,
      fixed,
      timeMs,
    }: {
      startedAt: string
      pages: readonly FlashBookPage[]
      results: readonly FlashBookPageResult[]
      ofSessionId: string
      fixed: number
      timeMs: number
    }) => {
      if (!enabled) return
      const base = latest.current
      if (!base) return
      apply(
        recordPractice(base, {
          startedAt,
          pages,
          results,
          ofSessionId,
          fixed,
          timeMs,
          bookTitle,
        }),
      )
      flush()
    },
    [apply, bookTitle, enabled, flush],
  )

  const summary: FlashBookReadSummary | null = summariseProgress(progress)

  return {
    /** What has been read, for the cover to describe. */
    summary,
    /** Where a continue would land, or `null` when there is nothing to continue. */
    resume: resumeTarget(progress),
    /** How far into its exercises the open read is. */
    openPct: openReadPct(progress, pages),
    /** The book's progress as stored, for a surface that needs the register. */
    progress,
    begin,
    adopt,
    note,
    finish,
    practice,
  }
}
